import type { ReferenceType } from '@cult/domain';
import { type ReferenceItem, ReferenceListResponseSchema } from '@cult/schemas';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../../lib/api-client';
import { timeoutSignal } from '../../../lib/timeout-signal';
import { referenceKeys } from '../reference-keys';

// fetch has no timeout of its own: a request a proxy holds open would leave the query pending forever,
// with an empty dropdown and no error. Abort it instead so it fails, retries once, then surfaces.
export const REFERENCE_TIMEOUT_MS = 10_000;

/** Active items of one reference type (categories, core values, frameworks), in admin sort order. */
export const useReferenceItems = (type: ReferenceType) => {
  return useQuery<ReferenceItem[]>({
    queryKey: referenceKeys.list(type),
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(`/reference/${type}`, {
        // Keep React Query's signal so unmount/cancel still aborts the request.
        signal: timeoutSignal(REFERENCE_TIMEOUT_MS, signal),
      });
      return ReferenceListResponseSchema.parse(raw).items;
    },
    staleTime: 5 * 60_000,
    // One retry after a timeout or any other failure; the second failure becomes the query error.
    retry: 1,
  });
};
